import { VariantProps, cva } from "class-variance-authority";
import { OverlayShadow } from "@/components/Molecules/OverlayShadow/Overlay";
import {
	FullBackgroundImage,
	FullBackgroundImageHero,
} from "@/components/Molecules/ImageCard";
import { ImageCopyCard } from "@/components/Molecules/HeroCopyCard";
import { useRef } from "react";
import styles from "./index.module.scss";
import { PageScroll } from "@/components/Atoms/PageScroll";
import { HeroAnchors } from "@/components/Molecules/Anchors";
import { Shape } from "@/components/Atoms/Spacer";
import SectionText from "../../Text";
import { GeoHandler } from "@/components/Templates/Main/GeoConditional";
import { sliderImageBgColorSelection } from "@/lib/fragments";
import { resolvePortfolioBackgroundStyle } from "@/lib/resolve-background-color";

const imageOverlayColor = { hex: "#000" } as const;

export type HeroImageVariantProps = VariantProps<typeof HeroImageVariants>;
const HeroImageVariants = cva(` relative flex flex-col  `, {
  variants: {
    heroHeight: {
      small: "min-h-[200px] lg:min-h-[350px] pt-[50px]",
      tall: `flex flex-row items-center min-h-[350px] md:h-[650px] lg:pt-[100px] mt-[90px]`,
      content: `pt-24`,
      default: "min-h-[60vh] md:min-h-[100dvh]  flex flex-col",
    },
    textFlexPosition: {
      start: "justify-start",
      end: "justify-end",
      center: "justify-center",
    },
  },
  defaultVariants: {
    heroHeight: "default",
    textFlexPosition: "center",
  },
});

interface Props {
  id: string;
  bgColor: any;
  bgColorGr?: any;
  heroHeight: "small" | "tall" | "content" | "default";
  textFlexPosition: "start" | "end" | "center";
  textAlignment:
    | "default"
    | "center"
    | "left"
    | "right"
    | "fixedCenter"
    | "featuredCenter"
    | null
    | undefined;
  overlay: string;
  swapBgForColor: boolean;
  swapTitleForImage: boolean;
  imageReplacerPosition: string;
  image: any;
  imageReplacer: any;
  title: string;
  description: string;
  label: string;
  textColor: any;
  labelColor: any;
  textWidth: string;
  useLabelBeforeTitle: boolean;
  underline: any;
  textAlign: string;
  addScrollIcon: boolean;
  scrollIcon: any;
  scrollIconType: string;
  positionIcon?: "bottom" | "center" | string;
  iconColor: any;
  addTitleUnderline: boolean;
  customUnderlineAsset: any;
  underlineColor: any;
  addCustomIconBackgroundElement: boolean;
  iconBackgroundAsset: any;
  iconBackgroundColor: any;
  customScrollIcon: any;
  anchors: any;
  anchorsLabelColor: any;
  anchorsType: string;
  anchorsStyleColor: any;
  addSpacer: boolean;
  customShape: any;
  spacerDirection: "inner" | "outer" | "merge";
  spacerConfiguration: any;
  addAdditionalContent: boolean;
  additionalContent: any;
  imageGeo: any;
  alternativeImage: any;
  invertOnX: boolean;
  hideTitle: boolean;
  imagePosition?: string;
}

type HeroImageDisplayProps = Pick<
  Props,
  "image" | "imageGeo" | "alternativeImage"
>;

export default function HeroImage({
  textFlexPosition,
  heroHeight,
  spacerDirection,
  ...props
}: Props) {
  const scrollRef: any = useRef(null);
  const executeScroll = () =>
    scrollRef.current.scrollIntoView({
      block: "start",
      behavior: "smooth",
      inline: "start",
    });

  const isContain = props.imagePosition === "Contain";
  const displayImage = resolveDisplayImage(props);
  const backgroundStyle = resolvePortfolioBackgroundStyle(
    props.bgColorGr,
    props.bgColor
  );

  function resolveDisplayImage(heroProps: HeroImageDisplayProps) {
    if (!heroProps.image) return null;

    const geo = heroProps.imageGeo?.[0];
    const isToShow = GeoHandler({
      list: geo?.listOfCountries,
      country: geo?.countries,
      initial: geo?.countryAvailability,
    });

    return isToShow ? heroProps.image : heroProps.alternativeImage;
  }

  function heroImage() {
    if (!displayImage) return null;

    return (
      <FullBackgroundImageHero
        padding="90px"
        position={props.imagePosition}
        data={displayImage}
      />
    );
  }

  const copyCard = !props.hideTitle ? (
    <ImageCopyCard
      className={`${
        props.spacerConfiguration?.spacerDirection == "merge"
          ? " mn-12 lg:mb-20"
          : ""
      } ${isContain ? styles.containCopyCard : ""}`}
      flex={textFlexPosition}
      title={props.title}
      description={props.description}
      label={props.label}
      swapTitle={props.swapTitleForImage}
      imageReplacer={props.imageReplacer}
      imagePosition={props.imageReplacerPosition}
      color={props.textColor?.color}
      labelColor={props.labelColor?.color}
      layout={props.textAlignment}
      width={props.textWidth}
      labelPosition={props.useLabelBeforeTitle}
      underline={[
        props.addTitleUnderline,
        props.customUnderlineAsset,
        props.underlineColor,
      ]}
    />
  ) : null;

  return (
    <>
      <div
        id={props.id}
        style={backgroundStyle}
        className={` ${HeroImageVariants({
          heroHeight,
          textFlexPosition,
        })} relative`}
      >
        {props.overlay && (
          <OverlayShadow
            format={props.textAlignment}
            data={props.overlay}
            color={imageOverlayColor}
            layout={props.textAlignment}
          />
        )}
        {isContain ? (
          <div className={styles.containLayout}>
            <div className={styles.containGrid}>
              <div className={styles.containCopyCol}>{copyCard}</div>
              {!props.swapBgForColor && displayImage && (
                <figure className={styles.containImageCol}>
                  <FullBackgroundImage
                    data={displayImage}
                    position="Contain"
                  />
                </figure>
              )}
            </div>
          </div>
        ) : (
          <>
            {props.swapBgForColor ? <></> : <>{heroImage()}</>}
            {copyCard}
          </>
        )}

        {props.addSpacer ? (
          <>
            {props.addScrollIcon && (
              <div className={``} ref={scrollRef}>
                <PageScroll
                  onClick={() => executeScroll()}
                  spacer={props.addSpacer}
                  type={props.scrollIcon?.scrollIconType}
                  color={props.scrollIcon?.iconColor?.color}
                  bg={[
                    props.scrollIcon?.addCustomIconBackgroundElement,
                    props.scrollIcon?.iconBackgroundAsset,
                    props.scrollIcon?.iconBackgroundColor?.color,
                  ]}
                  custom={props.scrollIcon?.customScrollIcon?.img}
                  featured={props.scrollIcon?.iconFeatured}
                  positionIcon={props.positionIcon}
                />
              </div>
            )}

            <Shape
              shape={props.spacerConfiguration?.customShape}
              color={props.bgColor}
              direction={props.spacerConfiguration?.spacerDirection}
              icon={[
                props.spacerConfiguration?.addIconElement,
                props?.spacerConfiguration?.spacerIcon,
              ]}
              position={props.spacerConfiguration?.spacerPosition}
              iconPosition={props.spacerConfiguration?.iconPosition}
              invert={false}
              invertX={props.invertOnX}
            />
          </>
        ) : (
          <>
            {props.addScrollIcon && (
              <div
                className={`absolute bottom-0 h-[45px] z-10 w-full left-0 right-0`}
                ref={scrollRef}
              >
                <PageScroll
                  onClick={() => executeScroll()}
                  spacer={props.addSpacer}
                  type={props.scrollIcon?.scrollIconType}
                  color={props.scrollIcon?.iconColor?.color}
                  bg={[
                    props.scrollIcon?.addCustomIconBackgroundElement,
                    props.scrollIcon?.iconBackgroundAsset,
                    props.scrollIcon?.iconBackgroundColor?.color,
                  ]}
                  custom={props.scrollIcon?.customScrollIcon?.img}
                  featured={props.scrollIcon?.iconFeatured}
                  inner={props.scrollIcon?.iconInner}
                  positionIcon={props.positionIcon}
                />
              </div>
            )}
          </>
        )}
      </div>
      {props.anchors && (
        <HeroAnchors
          data={props.anchors}
          color={props.anchorsLabelColor?.color}
          type={props.anchorsType}
          styleColor={props.anchorsStyleColor}
          bg={props.bgColor?.color}
        />
      )}
      {props.addAdditionalContent && (
        <SectionText data={props.additionalContent} />
      )}
    </>
  );
}

export const fragment = `
    fragment HeroImageFragment on SectionHeroImageRecord{
      __typename
      id
      name
      title
      description
      hideTitle
      addAdditionalContent
      additionalContent {
      __typename
      id
      addTitle
      title
      addTitleUnderline
      customUnderlineAsset {
        img {
          url
          width
          height
        }
      }
      underlineColor {
        color {
          hex
        }
        name
      }
      text(markdown: true)
      layout
      textAlignment
      textColumnLayout
      textColumnDisplay
      useSectionMargins
      useExtraSectionMargins
	  useExtraBottomSectionMargins
      textColor{
        color{
          hex
        }
      }
    bgColor{
      color{
        hex
      }
    }
      }
      textColor{
        color{
          hex
        }
      }
      labelColor{
        color{
          hex
        }
      }
      heroHeight
      imagePosition
      showQuote
      label
        overlay
        textWidth
        textAlignment
        textFlexPosition
        useLabelBeforeTitle
        swapTitleForImage
        imageReplacerPosition
        bgColor{
          color{
            hex
            cssRgb
          }
        }
        bgColorGr {
          ${sliderImageBgColorSelection}
        }
        swapBgForColor
        addSpacer
        invertOnX
        spacerConfiguration {
          id
          spacerDirection
          spacerPosition
          customShape {
            img {
              alt
              url
              width
              height
            }
          }
          addIconElement
          spacerIcon {
            img {
              alt
              url
              width
              height
            }
          }
          iconPosition
        }
    addScrollIcon
    positionIcon
    scrollIcon {
      scrollIconType
      iconInner
      iconColor {
        color {
          hex
        }
      }
      iconBackgroundColor{
        color{
          hex
        }
      }
      addCustomIconBackgroundElement
      iconBackgroundAsset{
        img{
          url
        }
      }
      customScrollIcon{
        img{
          url
        }
      }
	  iconFeatured
    }
        addTitleUnderline
        underlineColor{
          color{
            hex
          }
        }
        customUnderlineAsset{
          img{
            url
            width
            height
          }
        }
        anchors {
          ... on SectionImageRecord {
            id
            title
          }
          ... on SectionTextGalleryRecord {
            id
            title
          }
          ... on SectionTextImageRecord {
            id
            title
          }
          ... on SectionBrandArticleRecord{
            id
            title          
          }
        }
        anchorsType
        anchorsLabelColor{
          color{
            hex
          }
        }
        anchorsStyleColor{
          color{
            hex
          }
        }
        useSpacer
        imageReplacer {
          title
          alt
          url
          format
          responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
            ... responsiveImageFragment
          }
        }
      image {
        id
        title
        alt
        tags
        format
        focalPoint {
          y
          x
        }
        responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
          ... responsiveImageFragment
        }
      }
      imageGeo {
        countryAvailability
        listOfCountries
        countries {
          countryCode
        }
      }
      alternativeImage {
        id
        title
        alt
        tags
        format
        focalPoint {
          y
          x
        }
        responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 2400 }, sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1920px") {
          ... responsiveImageFragment
        }
      }
    }
`;
